import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Box, ListItemButton, ListItemText, Tooltip, Typography } from "@mui/material";
import format from "date-fns/format";
import React from "react";
import { useTranslation } from "react-i18next";
import { areEqual } from "react-window";

import Highlight from "../../Highlight";
import { DailyDemand } from "../store/shift-demand-types";
import UpdateDemandInputCard from "./update-demand-input-card";

const BORDER_COLOR = "#E5E5E5";
const SIDEBAR_WIDTH = "210px";

const DemandVirtualizedRow = React.memo(({ index, style, data }: any) => {
	const { t } = useTranslation();
	const {
		items,
		toggleWorkcenter,
		handleDemandChange,
		editedDemands,
		expandedWorkcenters,
	} = data;

	const item = items[index];
	const containerStyle = {
		...style,
		backgroundColor: "#fff",
	};


	if (item.type === "WORKCENTER") {
		const isExpanded = expandedWorkcenters.has(item.id.toString());
		return (
			<ListItemButton
				onClick={() => toggleWorkcenter(item.id.toString())}
				style={containerStyle}
				sx={{
					backgroundColor: "#e3e6ed",
					"&:hover": { backgroundColor: "#d1d5dd" },
				}}
			>
				{isExpanded ? <ExpandLess /> : <ExpandMore />}
				<ListItemText
					primary={
						<Box sx={{ display: "flex", alignItems: "center", gap: "12px", ml: 1, }}>
							<Typography
                                sx={{
                                    fontFamily: "Roboto",
                                    fontWeight: 500,
                                    fontSize: "20px",
                                    lineHeight: "160%",
                                    letterSpacing: "0.15px",
                                  }}                                  
                            >
                                <Highlight searchText={item.searchText} value={item.name} />
							</Typography>
							<Typography
                                sx={{
                                    fontFamily: "Roboto",
                                    fontWeight: 400,
                                    fontSize: "13px",
                                    lineHeight: "18px",
                                    letterSpacing: "0.16px",
                                  }}
                            >
								{t("shift-demand_jobs-count", { count: item.jobCount || 0 })}
							</Typography>
						</Box>
					}
				/>
			</ListItemButton>
		);
	}

	if (item.type === "PATTERN_ROW") {
		const { jobName, pattern, isLastPattern, searchText } = item;
		
		return (
			<Box style={containerStyle} sx={{ display: "flex", width: "100%", padding: "6px 0px", boxSizing: "border-box", borderBottom: isLastPattern ? `1px solid ${BORDER_COLOR}` : "none", }}>
				{/* Job Name */}
				<Box sx={{ width: SIDEBAR_WIDTH, display: "flex", alignItems: "center", padding: "12px 16px" }}>
					{item.showJobName && (
                        <Tooltip title={jobName}>
						<Typography
                        sx={{
                            fontFamily: "Roboto",
                            fontWeight: 500,
                            fontSize: "16px",
                            lineHeight: "175%",
                            letterSpacing: "0.15px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                          }}                          
                        >
                            <Highlight searchText={searchText} value={jobName} />
						</Typography>
                        </Tooltip>
					)}
				</Box>

				{/* Shift Pattern Details */}
				<Box sx={{ width: SIDEBAR_WIDTH, display: "flex", flexDirection: "column", justifyContent: "center", padding: "8px 12px" }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                            width: "100%",
                            border: `1px solid ${BORDER_COLOR}`,
                            borderRadius: "9px",
                            padding: "12px 16px",
                        }}
                    >
                        <Tooltip title={pattern.shiftPatternName}>
                        <Typography
                            sx={{
                                fontFamily: "Roboto",
                                fontWeight: 500,
                                fontSize: "16px",
                                lineHeight: "150%",
                                letterSpacing: "0.15px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "100%",
                              }}                              
                        >
                            <Highlight searchText={pattern.searchText} value={pattern.shiftPatternName} />
                        </Typography>
                        </Tooltip>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                            }}
                        >
                            <AccessTimeOutlinedIcon
                                sx={{
                                    fontSize: "16px",
                                    color: "#000000",
                                }}
                            />
                        <Typography
                            sx={{
                                fontFamily: "Roboto",
                                fontWeight: 400,
                                fontSize: "12px",
                                lineHeight: "143%",
                                letterSpacing: "0.17px",
                              }}                              
                        >
                        {format(new Date(pattern.startDateTime), "h:mm a")} -{" "}
                        {format(new Date(pattern.endDateTime), "h:mm a")}
                        </Typography>
                        </Box>
                    </Box>
				</Box>

				{/* Days with shift demand inputs */}
				<Box sx={{ display: "flex", flex: 1 }}>
					{Array.from({ length: 7 }).map((_, dayIndex) => {
						const dayDemand: DailyDemand | undefined =
							pattern.dailyDemands?.find(
								(d: DailyDemand) => d.dayIndex === dayIndex
							);
                        let value = 0;
                        if( editedDemands && editedDemands.length > 0 && dayDemand?.demandId ){
                            const editedDemand = editedDemands.find(
                                (d:any) => d.demandId === dayDemand.demandId
                            )
                            if (editedDemand) {
                                value = editedDemand.workersRequired;
                            } else {
                                value = dayDemand?.workersRequired;
                            }
                        } else {
                            value = dayDemand?.workersRequired ?? 0;
                        }

						return (
							<Box key={dayIndex} sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "72px" }}>
								{dayDemand && (
									<UpdateDemandInputCard
										key={dayDemand.demandId}
                                        workcenterId={item.workcenterId}
                                        jobId={item.jobId}
										value={value}
                                        demandId={dayDemand.demandId}
										onChange={handleDemandChange}
                                        shiftId={dayDemand.shiftId}
                                        shiftStartDateTime={dayDemand.shiftStartDateTime}
                                        isValueChanged={
                                            editedDemands.some(
                                                (d: any) => d.demandId === dayDemand.demandId
                                            )
                                        }
									/>
								)}
							</Box>
						);
					})}
				</Box>
			</Box>
		);
	}

	return null;
}, areEqual);

DemandVirtualizedRow.displayName = "DemandVirtualizedRow";
export default DemandVirtualizedRow;
